import { useMemo, useState } from 'react';
import {
  sendBroadcast,
  isWrongPasswordError,
  type BroadcastAudience,
  type BroadcastResult
} from '../../../lib/adminApi';
import type { DestKind } from './constants';

type AdminLeague = { leagueId: string; name: string; memberCount: number };
type AdminMiniLeague = { miniLeagueId: string; name: string; leagueName: string; memberCount: number };

export function useBroadcastForm(
  password: string,
  leagues: AdminLeague[],
  miniLeagues: AdminMiniLeague[],
  totalUsers: number
) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');

  const [destKind, setDestKind] = useState<DestKind>('none');
  const [destLeagueId, setDestLeagueId] = useState('');
  const [destMiniId, setDestMiniId] = useState('');
  const [customPath, setCustomPath] = useState('');

  const [audKind, setAudKind] = useState<'all' | 'league' | 'miniLeague'>('all');
  const [audLeagueId, setAudLeagueId] = useState('');
  const [audMiniId, setAudMiniId] = useState('');

  const [busy, setBusy] = useState<false | 'preview' | 'send'>(false);
  const [result, setResult] = useState<BroadcastResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [broadcastId, setBroadcastId] = useState(() => crypto.randomUUID());

  function touch<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setResult(null);
      setError(null);
      setConfirming(false);
      setBroadcastId(crypto.randomUUID());
    };
  }

  const path = useMemo(() => {
    switch (destKind) {
      case 'none':
        return '';
      case 'home':
        return '/';
      case 'football':
        return '/football';
      case 'leagues':
        return '/leagues';
      case 'leagues_new':
        return '/leagues/new';
      case 'pro':
        return '/pro';
      case 'league':
        return destLeagueId ? `/play/${destLeagueId}` : '';
      case 'miniLeague':
        return destMiniId ? `/leagues/${destMiniId}` : '';
      case 'custom':
        return customPath.trim();
    }
  }, [destKind, destLeagueId, destMiniId, customPath]);

  const audience: BroadcastAudience = useMemo(() => {
    if (audKind === 'league') return { kind: 'league', leagueId: audLeagueId };
    if (audKind === 'miniLeague') return { kind: 'miniLeague', miniLeagueId: audMiniId };
    return { kind: 'all' };
  }, [audKind, audLeagueId, audMiniId]);

  const estSize = useMemo(() => {
    if (audKind === 'all') return totalUsers;
    if (audKind === 'league') return leagues.find((l) => l.leagueId === audLeagueId)?.memberCount ?? 0;
    return miniLeagues.find((m) => m.miniLeagueId === audMiniId)?.memberCount ?? 0;
  }, [audKind, audLeagueId, audMiniId, totalUsers, leagues, miniLeagues]);

  const destNeedsPick =
    (destKind === 'league' && !destLeagueId) ||
    (destKind === 'miniLeague' && !destMiniId) ||
    (destKind === 'custom' && !customPath.trim());
  const audNeedsPick =
    (audKind === 'league' && !audLeagueId) || (audKind === 'miniLeague' && !audMiniId);
  const pathInvalid = !!path && !path.startsWith('/');
  const canSubmit =
    !!title.trim() && !!body.trim() && !destNeedsPick && !audNeedsPick && !pathInvalid && !busy;

  async function run(dryRun: boolean) {
    if (!canSubmit) return;
    setBusy(dryRun ? 'preview' : 'send');
    setError(null);
    try {
      const res = await sendBroadcast(password, {
        title: title.trim(),
        body: body.trim(),
        path: path || undefined,
        ctaLabel: ctaLabel.trim() || undefined,
        audience,
        broadcastId,
        dryRun
      });
      setResult(res);
      setConfirming(false);
    } catch (e) {
      setError(
        isWrongPasswordError(e)
          ? 'Admin password rejected (it may have rotated). Lock and sign in again.'
          : (e as { message?: string })?.message || 'Could not send. Check the sendBroadcast logs.'
      );
    } finally {
      setBusy(false);
    }
  }

  return {
    title,
    body,
    ctaLabel,
    destKind,
    destLeagueId,
    destMiniId,
    customPath,
    audKind,
    audLeagueId,
    audMiniId,
    busy,
    result,
    error,
    confirming,
    path,
    pathInvalid,
    audNeedsPick,
    estSize,
    canSubmit,
    touch,
    setTitle,
    setBody,
    setCtaLabel,
    setDestKind,
    setDestLeagueId,
    setDestMiniId,
    setCustomPath,
    setAudKind,
    setAudLeagueId,
    setAudMiniId,
    setConfirming,
    run
  };
}

export type BroadcastForm = ReturnType<typeof useBroadcastForm>;
